import { createContext, useContext, useEffect, useState } from 'react'
import { io } from 'socket.io-client'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'
import { t } from '../i18n'
import { playOrderSound, unlockSound } from '../utils/orderSound'

const SocketContext = createContext()

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [orders, setOrders] = useState([])
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (isAuthenticated && user) {
      const socketInstance = io(import.meta.env.VITE_API_URL || 'https://api.rawajbusiness.com', {
        transports: ['websocket'],
        // Read on every (re)connect, so a refreshed login token is used.
        auth: cb => cb({ token: localStorage.getItem('token') })
      })

      socketInstance.on('connect', () => {
        console.log('Socket connected:', socketInstance.id)
        // The server puts this connection in its own store's rooms.
        setIsConnected(true)
      })

      socketInstance.on('disconnect', () => {
        console.log('Socket disconnected')
        setIsConnected(false)
      })

      // An expired login token is refused and socket.io does not retry on its
      // own; the API refreshes the token on its next request, so try again
      // shortly with the new one (backing off, up to a minute apart).
      let retries = 0
      let retryTimer = null
      socketInstance.on('connect', () => { retries = 0 })
      socketInstance.on('connect_error', (error) => {
        setIsConnected(false)
        if (error?.message === 'unauthorized' && localStorage.getItem('token')) {
          clearTimeout(retryTimer)
          retryTimer = setTimeout(() => socketInstance.connect(), Math.min(60000, 2000 * 2 ** retries++))
          return
        }
        console.error('Socket connection error:', error)
      })

      // Order events
      socketInstance.on('order.created', (order) => {
        console.log('New order created:', order)
        setOrders(prev => [order, ...prev])
        // A sale this person rang up themselves needs no alert.
        if (order.placed_by && order.placed_by === user.id) return
        playOrderSound()
        toast.success(order.table_number
          ? t("New order: {order_code} - Table {table_number}", { order_code: order.order_code, table_number: order.table_number })
          : t("New order: {order_code}", { order_code: order.order_code }), { duration: 6000 })
      })

      socketInstance.on('booking.created', (booking) => {
        playOrderSound()
        toast.success(booking.kind === 'reservation' ? t("New table reservation") : t("New booking"), { duration: 6000 })
      })

      socketInstance.on('order.updated', (order) => {
        console.log('Order updated:', order)
        setOrders(prev => 
          prev.map(o => o.id === order.id ? { ...o, ...order } : o)
        )
        
        if (user.role === 'kitchen') {
          toast(t("Order {order_code}: {status}", { order_code: order.order_code, status: t(order.status) }))
        }
      })

      socketInstance.on('order.confirmed', (order) => {
        console.log('Order confirmed:', order)
        setOrders(prev => 
          prev.map(o => o.id === order.id ? { ...o, ...order } : o)
        )
        
        if (user.role === 'kitchen') {
          toast.success(t("Order {order_code} confirmed - Start preparing!", { order_code: order.order_code }))
        }
      })

      socketInstance.on('order.cancelled', (order) => {
        console.log('Order cancelled:', order)
        setOrders(prev => 
          prev.map(o => o.id === order.id ? { ...o, ...order } : o)
        )
        
        if (user.role === 'kitchen') {
          toast.error(t("Order {order_code} cancelled", { order_code: order.order_code }))
        }
      })

      // Payment events
      socketInstance.on('payment.recorded', (data) => {
        console.log('Payment recorded:', data)
        setOrders(prev => 
          prev.map(o => o.id === data.orderId ? { ...o, payment_status: data.paymentStatus } : o)
        )
        
        if (['admin', 'manager', 'cashier'].includes(user.role)) {
          toast.success(t("Payment recorded: {totalPaid}", { totalPaid: data.totalPaid }))
        }
      })

      // Kitchen acknowledgment
      socketInstance.on('kitchen.ack', (data) => {
        console.log('Kitchen acknowledgment:', data)
        setOrders(prev => 
          prev.map(o => o.id === data.orderId ? { ...o, status: 'PREPARING' } : o)
        )
      })

      // Printer events
      socketInstance.on('printer.status', (data) => {
        console.log('Printer status:', data)
        if (data.status === 'error') {
          toast.error(t("Printer {printerId} error: {error}", { printerId: data.printerId, error: data.error }))
        }
      })

      setSocket(socketInstance)

      return () => {
        clearTimeout(retryTimer)
        socketInstance.disconnect()
        setSocket(null)
        setIsConnected(false)
      }
    }
  }, [isAuthenticated, user])

  // Browsers block sound until the page has been clicked or typed on once.
  useEffect(() => {
    const unlock = () => unlockSound()
    window.addEventListener('pointerdown', unlock, { once: true })
    window.addEventListener('keydown', unlock, { once: true })
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [])

  const updateOrderStatus = (orderId, status) => {
    if (socket) {
      socket.emit('order-status-update', {
        orderId,
        status,
        userId: user?.id
      })
    }
  }

  const acknowledgeOrder = (orderId) => {
    if (socket && user?.role === 'kitchen') {
      socket.emit('kitchen-ack', {
        orderId,
        userId: user.id
      })
    }
  }

  const updatePaymentStatus = (orderId, paymentStatus) => {
    if (socket) {
      socket.emit('payment-update', {
        orderId,
        paymentStatus,
        userId: user?.id
      })
    }
  }

  const joinBranch = (branchId) => {
    if (socket) {
      socket.emit('join-branch', branchId)
    }
  }

  const value = {
    socket,
    isConnected,
    orders,
    updateOrderStatus,
    acknowledgeOrder,
    updatePaymentStatus,
    joinBranch
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}

export function useSocket() {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}
